import { useContext } from 'react'
import { persistCache, useCacheRestorer } from '../../graphql/cache'
import { Error as UserLocationError, UserLocationContext } from '../../contexts/user-location-context'
import { useSearchPage } from '../../forms/hocs/search-page.context'
import { useSearchViewState } from '../../contexts/search-view-state-context'
import { useFederatedQuery as useQuery } from '@at/sauron-platform-graphql'
import { useListingsQueryVariables } from './use-listing-query-variables'
import {
    SearchResultsListingsGridQuery,
    SearchResultsListingsGridQueryVariables,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import {
    SearchResultsListingsGridQueryGql,
    SearchResultsLeasingListingsGridQueryGql,
} from '../../contexts/search-results-context/query/search-results-listings.fed.query'
import { useIsLeasingSearch } from '../../utils/is-leasing-search'

export const useSearchResultsQuery = () => {
    const { cacheInitialised } = useCacheRestorer()
    const { error } = useContext(UserLocationContext)
    const { channel, replacePage } = useSearchPage()
    const { showSearchFilter } = useSearchViewState()
    const listingVariables = useListingsQueryVariables()
    const isLeasingSearch = useIsLeasingSearch()

    // TODO: Handle 'location=GLOBAL' for validation
    // TODO: Toggle location=GLOBAL doesn't put postcode/lat-long back in the URL

    const query = isLeasingSearch ? SearchResultsLeasingListingsGridQueryGql : SearchResultsListingsGridQueryGql
    const variables = {
        ...listingVariables,
        useGridLayout: true,
    }
    const skip = !cacheInitialised || error === UserLocationError.CLIENT || showSearchFilter || !listingVariables.page
    const { data, previousData, loading, fetchMore, ...rest } = useQuery<
        SearchResultsListingsGridQuery,
        SearchResultsListingsGridQueryVariables
    >(query, {
        variables,
        skip,
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all',
    })

    const _fetchMore = async (page: number, previous = false) => {
        if (loading) {
            return
        }

        if (!previous) {
            replacePage(page)
        }

        const result = await fetchMore({
            variables: {
                page,
            },
        })

        await persistCache()

        return result
    }

    return {
        data: error !== UserLocationError.NONE ? undefined : data,
        previousData: previousData,
        loading: !cacheInitialised || loading,
        totalResults:
            (loading ? previousData?.searchResults?.page?.results?.count : data?.searchResults?.page?.results?.count) ||
            0,
        channel,
        fetchMore: _fetchMore,
        ...rest,
        variables: listingVariables,
    }
}
